import { createContext, useCallback, useContext, useRef, useState, useTransition } from 'react';
import InsufficientBalanceModal, { INSUFFICIENT_BALANCE_MODAL_ID } from '../components/InsufficientBalanceModal';
import { AssetCreationName, GenerationInstrument } from '@/types/media';
import { AssetCreationToolName } from '@/types/media';
import { ADD_MEDIA_MODAL_ID } from '@/features/media-modal/components/AddMediaModal';
import { useBillingContext } from '../components/BillingContext';
import { PricesService } from '../services';
import StudioRoutes from '@/utils/routes';
import useRouterNavigation from '@/hooks/useRouterNavigation';

export const useInsufficientBalance = () => useContext(InsufficientBalanceContext);

const InsufficientBalanceContext = createContext<{
  creationName: AssetCreationToolName | AssetCreationName | null;
  checkBalance: (
    name: AssetCreationToolName | AssetCreationName,
    instrumentsUsed?: GenerationInstrument[],
    beforeUpgrade?: () => void,
    generationsCount?: number
  ) => boolean;
  handleUpgrade: () => void;
}>({
  creationName: null,
  checkBalance: () => false,
  handleUpgrade: () => {},
});

export const InsufficientBalanceContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { remainingCredits } = useBillingContext();
  const beforeUpgradeRef = useRef<(() => void) | null>(null);
  const [isPreparingUpgrade, setIsPreparingUpgrade] = useState(false);
  const [isTransitioningToPlans, startTransition] = useTransition();
  const [creationName, setCreationName] = useState<AssetCreationToolName | AssetCreationName | null>(null);
  const { pushPath } = useRouterNavigation();
  const checkBalance = useCallback(
    (
      name: AssetCreationToolName | AssetCreationName,
      instrumentsUsed: GenerationInstrument[] = [],
      beforeUpgrade?: () => void,
      generationsCount = 1
    ) => {
      const price = PricesService.getPrice(name, instrumentsUsed) * generationsCount;
      if (!remainingCredits || remainingCredits < price) {
        setCreationName(name);
        if (beforeUpgrade) beforeUpgradeRef.current = beforeUpgrade;
        shopify.modal.hide(ADD_MEDIA_MODAL_ID);
        shopify.modal.show(INSUFFICIENT_BALANCE_MODAL_ID);
        return false;
      }
      return true;
    },
    [remainingCredits]
  );

  const handleUpgrade = useCallback(async () => {
    setIsPreparingUpgrade(true);
    if (beforeUpgradeRef.current) {
      await beforeUpgradeRef.current();
      beforeUpgradeRef.current = null;
    }
    setIsPreparingUpgrade(false);
    startTransition(() => {
      pushPath(StudioRoutes.plans());
      shopify.modal.hide(INSUFFICIENT_BALANCE_MODAL_ID);
    });
  }, [beforeUpgradeRef, pushPath]);

  return (
    <InsufficientBalanceContext.Provider value={{ checkBalance, creationName, handleUpgrade }}>
      <InsufficientBalanceModal isLoading={isPreparingUpgrade || isTransitioningToPlans} />
      {children}
    </InsufficientBalanceContext.Provider>
  );
};
